import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useCallback, useState} from 'react';
// eslint-disable-next-line
import {Checkbox, Divider, Label, Popup} from 'semantic-ui-react';

import * as GroupSelectionsActions from '../../state/views/groupSelections/actions';
import type * as GroupSelectionsTypes from '../../state/views/groupSelections/types';
import * as ViewHooks from '../../state/views/hooks';
import {useViewAction} from '../../state/views/hooks';
import {DD_PRIVACY_ALLOW} from '../../util/datadog/privacy';
import type * as QueryTypes from '../../util/queryTypes';
import {type Config as TableSettings} from '../../util/runfeed';
import * as Run from '../../util/runs';
import * as RunTypes from '../../util/runTypes';
import {NamedProjectFieldSelector} from '../ProjectFieldSelector';
import {
  WBTableActionGroupButton,
  WBTableActionGroupPicker,
} from './WBTable/WBTableActionsGroup';

interface RunsGroupPickerProps {
  entityName: string;
  projectName: string;
  tableSettings: TableSettings;
  groupSelectionsRef: GroupSelectionsTypes.Ref;
  recommendedGrouping?: QueryTypes.Grouping; // A good grouping for the current data
  onGroupingChanged(): void;
}

export const RunsGroupPicker = (props: RunsGroupPickerProps) => {
  const {
    entityName,
    projectName,
    tableSettings,
    groupSelectionsRef,
    onGroupingChanged,
  } = props;

  const defaultKeys = [
    Run.keyToString(RunTypes.GROUP_BY_ALL_KEY),
    'run:displayName',
    'run:state',
    'run:username',
    'run:sweep',
    'run:group',
    'run:jobType',
    'run:host',
  ];

  const groupSelections = ViewHooks.usePart(groupSelectionsRef);
  const setGrouping = useViewAction(
    groupSelectionsRef,
    GroupSelectionsActions.setGrouping
  );

  const setGroupingWrapped = useCallback(
    (...args: Parameters<typeof setGrouping>) => {
      setGrouping(...args);
      onGroupingChanged();
    },
    [setGrouping, onGroupingChanged]
  );

  const setGroupingWrappedString = useCallback(
    (args: string[]) =>
      setGroupingWrapped(args.map(key => Run.keyFromString(key)!)),
    [setGroupingWrapped]
  );

  return (
    <WBTableActionGroupPicker
      grouping={groupSelections.grouping}
      recommendedGrouping={props.recommendedGrouping}
      setGrouping={setGroupingWrapped}
      dropdown={
        <NamedProjectFieldSelector
          data-test="group-dropdown"
          entityName={entityName}
          projectName={projectName}
          tableSettings={tableSettings}
          placeholder="Select..."
          defaultKeys={defaultKeys}
          reorderable
          selection
          multi={true}
          value={groupSelections.grouping.map(key => Run.keyToString(key))}
          setValue={setGroupingWrappedString}
          searchByKeyAndText
        />
      }
    />
  );
};

type RunsGroupTableActionProps = RunsGroupPickerProps & {
  compact?: boolean;
};

export const RunsGroupTableAction = (props: RunsGroupTableActionProps) => {
  const [open, setOpen] = useState(false);
  const {groupSelectionsRef} = props;
  const groupSelections = ViewHooks.usePart(groupSelectionsRef);

  return (
    <Popup
      basic
      className="wb-table-action-popup"
      // Portal-rendered, so it doesn't inherit the run table's allow; group
      // keys shown here are already exposed in the table itself.
      data-dd-privacy={DD_PRIVACY_ALLOW}
      on="click"
      position="bottom left"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      trigger={
        <WBTableActionGroupButton
          onPress={() => {
            setOpen(!open);
          }}
          compact={props.compact}
          groupOpen={open}
          grouping={groupSelections.grouping}
        />
      }
      content={
        <Tailwind>
          <div className="mb-10" data-test="group-popup">
            <div className="mb-8 font-semibold text-moon-800 dark:text-moon-200">
              Group runs by...
            </div>
            <RunsGroupPicker
              entityName={props.entityName}
              projectName={props.projectName}
              tableSettings={props.tableSettings}
              groupSelectionsRef={props.groupSelectionsRef}
              onGroupingChanged={props.onGroupingChanged}
              recommendedGrouping={props.recommendedGrouping}
            />
          </div>
        </Tailwind>
      }
      popperModifiers={{
        preventOverflow: {enabled: false},
        flip: {enabled: false},
      }}
    />
  );
};
